import React, { useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { connect } from 'scrivito'

import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { BasicInput } from '@/Atomic/Atoms/Inputs/BasicInput'
import { Button } from '@/Atomic/Molecules/Button'
import {
  FilterBuilder,
  useFilterBuilder,
  pruneBlankConditions,
  SavedFilterStructure,
} from '@/Atomic/Organisms/FilterBuilder'
import { Modal } from '@/Atomic/Organisms/Modal'
import { FilterToast } from '@/Atomic/Widgets/AdvancedSearchModal/FilterToast'
import { removeListObjectFilterConditions } from '@/Atomic/Widgets/AdvancedSearchModal/listObjectFilter'
import { SaveAsFilterModal } from '@/Atomic/Widgets/AdvancedSearchModal/SaveAsFilterModal'
import { useEntitySchema } from '@/hooks/useEntitySchema'

import './EditFilterModal.scss'

export interface EditFilterModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (filterName: string, filter: SavedFilterStructure) => void
  onSearch?: (filter: SavedFilterStructure) => void
  onSaveAsFilter?: (
    filterName: string,
    filter: SavedFilterStructure,
  ) => void | Promise<void>
  onClearFilter?: () => void
  initialFilter?: SavedFilterStructure
  initialFilterName?: string
  entityName: string
}

export const EditFilterModal: React.FC<EditFilterModalProps> = connect(
  ({
    isOpen,
    onClose,
    onSave,
    onSearch,
    onSaveAsFilter,
    onClearFilter,
    initialFilter,
    initialFilterName = '',
    entityName,
  }: EditFilterModalProps) => {
    const { t } = useTranslation()
    const { attributes: baseSchemaAttributes, relations: schemaRelations } =
      useEntitySchema(entityName)
    const [filterName, setFilterName] = useState(initialFilterName)
    const [filterNameError, setFilterNameError] = useState<string | null>(null)
    const [isSaveAsFilterModalOpen, setIsSaveAsFilterModalOpen] =
      useState(false)

    const {
      filter,
      setFilter,
      validateFilter,
      toSavedFormat,
      createEmptyFilter,
      errors,
      clearError,
      loadFromSavedFilter,
    } = useFilterBuilder({
      entityName,
      initialFilter,
      baseSchemaAttributes,
      schemaRelations,
    })

    // Reset state when modal opens
    useEffect(() => {
      if (!isOpen) return

      setFilterName(initialFilterName)
      setFilterNameError(null)

      if (initialFilter) {
        loadFromSavedFilter(initialFilter)
      } else {
        setFilter(createEmptyFilter())
      }
    }, [
      isOpen,
      initialFilter,
      initialFilterName,
      setFilter,
      createEmptyFilter,
      entityName,
      loadFromSavedFilter,
    ])

    const handleSave = () => {
      // Validate filter name
      if (!filterName.trim()) {
        setFilterNameError(t('editFilter.nameRequired'))
        return
      }

      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      const transformedFilter = removeListObjectFilterConditions(
        entityName,
        toSavedFormat(),
      )

      // Persist the filter. onSave selects it by id (?filter=), which is how a
      // saved filter drives the list — do NOT also apply it as an inline ?query=
      // draft: that second write is immediately overwritten by the id selection
      // in the same tick, but its transient fetch still fires and flashes stale
      // rows into the list before the id-mode result arrives.
      onSave(filterName.trim(), transformedFilter)
      onClose()

      // Show toast notification for saved filter
      toast(<FilterToast variant="saved" onClearFilter={onClearFilter} />)
    }

    const handleCancel = () => {
      onClose()
    }

    const handleSearch = () => {
      // Emptied builder → the intent is "remove the filter", not a broken row.
      // Drop the applied filter instead of validating the blank condition, same
      // as AdvancedSearchModal.
      if (!hasActiveConditions) {
        onClearFilter?.()
        onClose()
        return
      }

      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      const transformedFilter = removeListObjectFilterConditions(
        entityName,
        toSavedFormat(),
      )

      onSearch?.(transformedFilter)
      onClose()

      // Show toast notification for applied filter
      toast(<FilterToast variant="applied" onClearFilter={onClearFilter} />)
    }

    const handleSaveAsFilterClick = () => {
      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      setIsSaveAsFilterModalOpen(true)
    }

    const handleSaveAsFilter = async (filterName: string) => {
      const transformedFilter = removeListObjectFilterConditions(
        entityName,
        toSavedFormat(),
      )
      if (onSaveAsFilter) {
        await onSaveAsFilter(filterName, transformedFilter)
      }
      setIsSaveAsFilterModalOpen(false)
      onClose()

      // Show toast notification for saved filter
      toast(<FilterToast variant="saved" onClearFilter={onClearFilter} />)
    }

    const handleFilterNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterName(e.target.value)
      if (filterNameError) {
        setFilterNameError(null)
      }
    }

    const isCreating = !initialFilter

    const hasActiveConditions = useMemo(
      () =>
        filter.groups.some((group) =>
          group.conditions.some((cond) => cond.field !== ''),
        ),
      [filter],
    )

    // Dirty vs what was opened: the saved filter's conditions (Edit) or empty
    // (create). Blank conditions pruned on both sides so object identity and a
    // trailing empty row don't register as changes.
    const conditionsDirty = useMemo(
      () =>
        JSON.stringify(pruneBlankConditions(toSavedFormat())) !==
        JSON.stringify(pruneBlankConditions(initialFilter)),
      [toSavedFormat, initialFilter],
    )
    // Renaming an existing filter is also a change worth saving.
    const isDirty =
      conditionsDirty || filterName.trim() !== initialFilterName.trim()

    return (
      <>
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          className="edit-filter-modal"
          closeOnOutsideClick={!isSaveAsFilterModalOpen}
        >
          <Modal.Header
            title={
              isCreating ? t('editFilter.createTitle') : t('editFilter.title')
            }
          />
          <Modal.Body>
            <div
              className={classNames('filter-name-field', {
                'has-error': filterNameError,
              })}
            >
              <FieldLabel
                name="filter-name"
                label={t('editFilter.filterName')}
              />
              <BasicInput
                name="filter-name"
                value={filterName}
                onChange={handleFilterNameChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                  }
                }}
                placeholder={t('editFilter.filterNamePlaceholder')}
              />
              {filterNameError && (
                <span className="filter-name-error">{filterNameError}</span>
              )}
            </div>
            <FilterBuilder
              filter={filter}
              onChange={setFilter}
              entityName={entityName}
              errors={errors}
              onClearError={clearError}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outlined" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleSaveAsFilterClick}
              disabled={!hasActiveConditions}
            >
              {t('editFilter.saveAsNewFilter')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={!isDirty}
            >
              {t('common.apply')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!isDirty || !hasActiveConditions}
            >
              {isCreating
                ? t('editFilter.createTitle')
                : t('editFilter.saveChangesAndApply')}
            </Button>
          </Modal.Footer>
        </Modal>
        <SaveAsFilterModal
          isOpen={isSaveAsFilterModalOpen}
          onClose={() => setIsSaveAsFilterModalOpen(false)}
          onSave={handleSaveAsFilter}
          title={t('editFilter.saveAsNewFilter')}
          buttonText={t('editFilter.saveAsNewAndApply')}
        />
      </>
    )
  },
)
